import { useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { posApi, shiftApi } from '@/services/api';
import { useShiftStore } from '@/store/shiftStore';
import { Loader2, AlertTriangle } from 'lucide-react';
import DenominationCounter from '@/components/pos/DenominationCounter';

type Props = {
  onStarted: () => void;
  onCancel?: () => void;
};

export default function ShiftStartForm({ onStarted, onCancel }: Props) {
  const [cashPocket, setCashPocket] = useState('');
  const [openingAmount, setOpeningAmount] = useState('0');
  const [error, setError] = useState('');
  const setOpenShift = useShiftStore((s) => s.setOpenShift);
  const setShiftJustClosed = useShiftStore((s) => s.setShiftJustClosed);
  const qc = useQueryClient();

  const { data: pockets = [], isLoading } = useQuery({
    queryKey: ['cashPockets'],
    queryFn: async () => {
      const res = await posApi.getCashPockets();
      return res.data.message as { name: string; pocket_name: string; is_default: number }[];
    },
  });

  const mutation = useMutation({
    mutationFn: () =>
      shiftApi.startShift({
        cash_pocket: cashPocket,
        opening_amount: parseFloat(openingAmount) || 0,
      }),
    onSuccess: (res) => {
      const shift = res.data.message;
      setOpenShift(shift);
      setShiftJustClosed(false);
      qc.invalidateQueries({ queryKey: ['openShift'] });
      onStarted();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to start shift');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!cashPocket) {
      setError('Please select a cash pocket.');
      return;
    }
    mutation.mutate();
  };

  const handleDenomTotal = useCallback((total: number) => {
    setOpeningAmount(String(total));
  }, []);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 w-full">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">Start Shift</h2>
        <p className="text-sm text-gray-500 mt-1">Select your cash drawer and enter opening cash.</p>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-gray-500 py-4">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading cash pockets...
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cash Pocket</label>
              <select
                className="input-field"
                value={cashPocket}
                onChange={(e) => setCashPocket(e.target.value)}
                required
              >
                <option value="">Select pocket</option>
                {pockets.map((p) => (
                  <option key={p.name} value={p.name}>{p.pocket_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Opening Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="input-field"
                value={openingAmount}
                onChange={(e) => setOpeningAmount(e.target.value)}
                placeholder="0"
                required
              />
              <DenominationCounter onTotalChange={handleDenomTotal} className="mt-3" />
            </div>
          </>
        )}
        {error && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> {error}
          </p>
        )}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={mutation.isPending || isLoading}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Start Shift
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary">
              Back
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
